import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import {
  RELATION_API_PROVIDED_BY,
  RELATION_CHILD_OF,
  RELATION_CONSUMES_API,
  RELATION_DEPENDS_ON,
  RELATION_HAS_MEMBER,
  RELATION_HAS_PART,
  RELATION_MEMBER_OF,
} from '@backstage/catalog-model';
import { EmptyState, TabbedLayout } from '@backstage/core-components';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  EntityRelationWarning,
  EntitySwitch,
  hasCatalogProcessingErrors,
  hasRelationWarnings,
  isComponentType,
  isKind,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@tranzact/backstage-plugin-catalog-graph';
import {
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
  EntityUserProfileCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { Button, Grid } from '@material-ui/core';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
/* eslint-disable */
import {
  EntityTeamcityContent,
  isTeamcityAvailable,
} from '@tranzact/backstage-plugin-teamcity';
import {
  EntityOctopusDeployContent,
  isOctopusDeployAvailable,
} from '@tranzact/plugin-octopus-deploy';
/* eslint-enable */
import {
  EntityAdrContent,
  isAdrAvailable,
} from '@backstage-community/plugin-adr';
import {
  EntityVaultCard,
  isVaultAvailable,
} from '@backstage-community/plugin-vault';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import { EntitySonarQubeCard } from '@fabz26/plugin-sonarqube';
import {
  EntityPagerDutyCard,
  isPluginApplicableToEntity as isPagerDutyAvailable,
} from '@pagerduty/backstage-plugin';
import {
  EntityGithubInsightsContent,
  isGithubInsightsAvailable,
} from '@roadiehq/backstage-plugin-github-insights';
import {
  EntityGithubPullRequestsOverviewCard,
  EntityGithubPullRequestsTable,
  isGithubPullRequestsAvailable,
} from '@roadiehq/backstage-plugin-github-pull-requests';
import {
  EntityJiraOverviewCard,
  isJiraAvailable,
} from '@roadiehq/backstage-plugin-jira';
import { AccentuateWrapper as EntityLayoutWrapper } from '@tranzact/backstage-plugin-aceituna';
import { EntityReleaseVersion } from './EntityReleaseVersion';

const MISSING_ANNOTATION_READ_MORE_URL =
  'https://backstage.io/docs/features/software-catalog/descriptor';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <TabbedLayout>
    <TabbedLayout.Route path="/GithubActions" title="Github Actions">
      <EntitySwitch>
        <EntitySwitch.Case if={isGithubActionsAvailable}>
          <EntityGithubActionsContent />
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <EmptyState
            title="No CI/CD available for this entity"
            missing="info"
            description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
            action={
              <Button
                variant="contained"
                color="primary"
                href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
              >
                Read more
              </Button>
            }
          />
        </EntitySwitch.Case>
      </EntitySwitch>
    </TabbedLayout.Route>
    <TabbedLayout.Route path="/teamcity" title="teamcity">
      <EntitySwitch>
        <EntitySwitch.Case if={isTeamcityAvailable}>
          <Grid item xs={12}>
            <EntityTeamcityContent />
          </Grid>
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <EmptyState
            title="No CI/CD available for this entity"
            missing="info"
            description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
            action={
              <Button
                variant="contained"
                color="primary"
                href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
              >
                Read more
              </Button>
            }
          />
        </EntitySwitch.Case>
      </EntitySwitch>
    </TabbedLayout.Route>
    <TabbedLayout.Route path="/OctopusDeploy" title="Octopus Deploy">
      <EntitySwitch>
        <EntitySwitch.Case if={isOctopusDeployAvailable}>
          <Grid item xs={12}>
            <EntityOctopusDeployContent defaultLimit={10} />
          </Grid>
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <EmptyState
            title="No CI/CD available for this entity"
            missing="info"
            description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
            action={
              <Button
                variant="contained"
                color="primary"
                href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
              >
                Read more
              </Button>
            }
          />
        </EntitySwitch.Case>
      </EntitySwitch>
    </TabbedLayout.Route>
  </TabbedLayout>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={12}>
      <EntityReleaseVersion />
    </Grid>
    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard
        variant="gridItem"
        height={400}
        showArrowHeads
        mergeRelations
        kinds={['component', 'api', 'system', 'domain']}
      />
    </Grid>

    <Grid item md={6} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityGithubPullRequestsOverviewCard variant="gridItem" />
    </Grid>
    <Grid item md={6}>
      <EntitySonarQubeCard
        variant="gridItem"
        missingAnnotationReadMoreUrl={MISSING_ANNOTATION_READ_MORE_URL}
      />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/jira" if={isJiraAvailable} title="Jira">
      <Grid item md={12} xs={12}>
        <EntityJiraOverviewCard />
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/vault" if={isVaultAvailable} title="Vault">
      <Grid item md={6} xs={12}>
        <EntityVaultCard />
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      if={isKubernetesAvailable}
      title="Kubernetes"
    >
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/code-insights"
      if={isGithubInsightsAvailable}
      title="Code Insights"
    >
      <EntityGithubInsightsContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/pr-info"
      if={isGithubPullRequestsAvailable}
      title="PR Info"
    >
      <EntityGithubPullRequestsTable />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/pagerduty"
      if={isPagerDutyAvailable}
      title="Pagerduty"
    >
      <EntityPagerDutyCard readOnly disableChangeEvents />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

// const websiteEntityPage = (
//   <EntityLayout>
//     <EntityLayout.Route path="/" title="Overview">
//       {overviewContent}
//     </EntityLayout.Route>

//     <EntityLayout.Route path="/ci-cd" title="CI/CD">
//       {cicdContent}
//     </EntityLayout.Route>

//     <EntityLayout.Route path="/dependencies" title="Dependencies">
//       <Grid container spacing={3} alignItems="stretch">
//         <Grid item md={6}>
//           <EntityDependsOnComponentsCard variant="gridItem" />
//         </Grid>
//         <Grid item md={6}>
//           <EntityDependsOnResourcesCard variant="gridItem" />
//         </Grid>
//       </Grid>
//     </EntityLayout.Route>

//     <EntityLayout.Route path="/docs" title="Docs">
//       {techdocsContent}
//     </EntityLayout.Route>

//     <EntityLayout.Route path="/kubernetes" title="Kubernetes">
//       <EntityKubernetesContent refreshIntervalMs={30000} />
//     </EntityLayout.Route>
//     <EntityLayout.Route path="/code-insights" title="Code Insights">
//       <EntityGithubInsightsContent />
//     </EntityLayout.Route>
//     <EntityLayout.Route path="/pr-info" title="PR Info">
//       <EntityGithubPullRequestsTable />
//     </EntityLayout.Route>
//   </EntityLayout>
// );

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      if={isKubernetesAvailable}
      title="Kubernetes"
    >
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/code-insights"
      if={isGithubInsightsAvailable}
      title="Code Insights"
    >
      <EntityGithubInsightsContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/pr-info"
      if={isGithubPullRequestsAvailable}
      title="PR Info"
    >
      <EntityGithubPullRequestsTable />
    </EntityLayout.Route>

    <EntityLayout.Route path="/jira" if={isJiraAvailable} title="Jira">
      <Grid item md={12} xs={12}>
        <EntityJiraOverviewCard />
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const componentPage = (
  <EntitySwitch>
    {/* TODO: Temporal solution, we need to decide if we are going to handle them separately or as one view */}
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            height={400}
            mergeRelations={false}
            showArrowHeads
            relations={[
              RELATION_HAS_PART,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_DEPENDS_ON,
              RELATION_CHILD_OF,
              RELATION_HAS_MEMBER,
              RELATION_MEMBER_OF,
            ]}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={12}>
          <EntityMembersListCard showAggregateMembersToggle />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            height={400}
            showArrowHeads
            mergeRelations={false}
            kinds={['component', 'system', 'api', 'domain']}
            relations={[
              RELATION_HAS_PART,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_DEPENDS_ON,
              RELATION_CHILD_OF,
              RELATION_HAS_MEMBER,
              RELATION_MEMBER_OF,
            ]}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.LEFT_RIGHT}
        title="System Diagram"
        mergeRelations={false}
        showArrowHeads
        height={700}
        kinds={['component', 'system', 'api', 'domain']}
        relations={[
          RELATION_HAS_PART,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_DEPENDS_ON,
          RELATION_CHILD_OF,
          RELATION_HAS_MEMBER,
          RELATION_MEMBER_OF,
        ]}
      />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/pagerduty"
      if={isPagerDutyAvailable}
      title="Pagerduty"
    >
      <EntityPagerDutyCard readOnly disableChangeEvents />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const domainPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            height={400}
            mergeRelations={false}
            showArrowHeads
            kinds={['component', 'system', 'api', 'domain']}
            relations={[
              RELATION_HAS_PART,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_DEPENDS_ON,
              RELATION_CHILD_OF,
              RELATION_HAS_MEMBER,
              RELATION_MEMBER_OF,
            ]}
          />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);
const resourcePage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            maxDepth={1}
            mergeRelations
            showArrowHeads
            kinds={['component', 'system', 'api', 'domain', 'resource']}
          />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
